import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BRAND_LOGO, BRAND_NAME, BRAND_TAGLINE, BRAND_AI_NAME, BRAND_COLORS } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Puppl SaaS landing — a deliberately compact marketing surface.
 * The detailed marketing site is Phase 4; this is the "Phase 1 land here"
 * page that points new trainers at /signup and existing customers at /login.
 */
export default function Landing() {
  return (
    <div
      data-testid="puppl-landing"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(120% 80% at 90% -10%, #D8F1E5 0%, transparent 55%)," +
          "radial-gradient(80% 60% at -10% 110%, #FFF4D7 0%, transparent 60%)," +
          BRAND_COLORS.cream,
        color: BRAND_COLORS.ink,
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      <PupplNav />
      <main
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "72px 32px 120px",
          display: "grid",
          gap: 72,
        }}
      >
        <Hero />
        <Pillars />
        <Pricing />
        <SocialProof />
        <Faq />
        <CallToAction />
      </main>
      <PupplFooter />
    </div>
  );
}

function PupplNav() {
  return (
    <header
      style={{
        maxWidth: 1180,
        margin: "0 auto",
        padding: "24px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Link
        to="/"
        data-testid="puppl-logo"
        style={{ textDecoration: "none", color: BRAND_COLORS.ink, display: "flex", alignItems: "center", gap: 10 }}
      >
        <PupplMark size={36} />
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 24, letterSpacing: "-0.5px" }}>
          {BRAND_NAME}
        </span>
      </Link>
      <nav style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <Link
          to="/login"
          data-testid="nav-login"
          style={{
            color: BRAND_COLORS.ink,
            fontWeight: 600,
            textDecoration: "none",
            padding: "10px 18px",
            borderRadius: 999,
          }}
        >
          Sign in
        </Link>
        <Link
          to="/get-started"
          data-testid="nav-signup-cta"
          style={{
            background: BRAND_COLORS.ink,
            color: BRAND_COLORS.cream,
            fontWeight: 600,
            textDecoration: "none",
            padding: "10px 22px",
            borderRadius: 999,
            transition: "transform 180ms ease, box-shadow 180ms ease",
            display: "inline-block",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        >
          Start free trial →
        </Link>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section style={{ display: "grid", gap: 28, maxWidth: 820 }}>
      <span
        style={{
          alignSelf: "flex-start",
          background: BRAND_COLORS.tealSoft,
          color: BRAND_COLORS.tealDeep,
          padding: "8px 16px",
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.2px",
        }}
      >
        Built in Australia, by a dog trainer for dog trainers · powered by {BRAND_AI_NAME}
      </span>
      <h1
        style={{
          fontFamily: "'Fraunces', serif",
          fontSize: "clamp(44px, 6vw, 72px)",
          lineHeight: 1.02,
          fontWeight: 600,
          letterSpacing: "-1.5px",
          margin: 0,
        }}
      >
        Run your whole dog
        <br />
        training business
        <br />
        from <em style={{ color: BRAND_COLORS.teal, fontStyle: "italic" }}>one place</em>.
      </h1>
      <p style={{ fontSize: 20, lineHeight: 1.55, color: BRAND_COLORS.muted, margin: 0, maxWidth: 640 }}>
        {BRAND_TAGLINE} CRM, bookings, courses, an LMS, and {BRAND_AI_NAME} — your slightly cheeky AI sidekick who handles the boring stuff.
      </p>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8 }}>
        <Link
          to="/get-started"
          data-testid="hero-signup-cta"
          style={{
            background: BRAND_COLORS.teal,
            color: "#fff",
            padding: "16px 28px",
            borderRadius: 999,
            fontWeight: 600,
            fontSize: 17,
            textDecoration: "none",
            boxShadow: "0 8px 24px -8px rgba(51,192,149,0.55)",
            transition: "transform 180ms ease, background 180ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.background = BRAND_COLORS.tealDeep;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.background = BRAND_COLORS.teal;
          }}
        >
          Start your 14-day trial →
        </Link>
        <Link
          to="/login"
          data-testid="hero-signin"
          style={{
            background: "transparent",
            color: BRAND_COLORS.ink,
            padding: "16px 28px",
            borderRadius: 999,
            fontWeight: 600,
            fontSize: 17,
            textDecoration: "none",
            border: `1.5px solid ${BRAND_COLORS.ink}22`,
          }}
        >
          I already have an account
        </Link>
      </div>
      <small style={{ color: BRAND_COLORS.muted, opacity: 0.85 }}>
        No card required today. AUD pricing. Australian English everywhere.
      </small>
    </section>
  );
}

function Pillars() {
  const items = [
    {
      label: "CRM + bookings",
      body:
        "Clients, dogs, classes, services, packages, intake forms, calendar. Built by a dog trainer who got sick of bouncing between five tools — not adapted from a gym SaaS.",
    },
    {
      label: "Courses + LMS",
      body:
        "Self-paced video courses with drip schedules, quizzes, checklists. Auto-enrol clients when they pay for the matching service.",
    },
    {
      label: `Meet ${BRAND_AI_NAME}`,
      body:
        `${BRAND_AI_NAME} drafts session reports, replies to enquiries, schedules from voice notes, and keeps the admin off your weekends.`,
    },
    {
      label: "Payments + emails",
      body:
        "Stripe for clients, Resend for transactional emails. Each tenant brings their own — money flows straight to your bank.",
    },
  ];
  return (
    <section style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
      {items.map((p) => (
        <article
          key={p.label}
          style={{
            background: BRAND_COLORS.paper,
            border: `1px solid ${BRAND_COLORS.border}`,
            borderRadius: 18,
            padding: "26px 24px",
            transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 16px 32px -20px rgba(15,22,20,0.18)";
            e.currentTarget.style.borderColor = BRAND_COLORS.teal + "55";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = BRAND_COLORS.border;
          }}
        >
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, margin: 0, marginBottom: 8, fontWeight: 600 }}>
            {p.label}
          </h3>
          <p style={{ color: BRAND_COLORS.muted, lineHeight: 1.55, margin: 0, fontSize: 15 }}>{p.body}</p>
        </article>
      ))}
    </section>
  );
}

function Pricing() {
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);

  useEffect(() => {
    axios.get(`${API}/public/plans`)
      .then((r) => { setPlans(r.data.plans || []); setTrialDays(r.data.trial_days || 14); })
      .catch(() => {});
  }, []);

  return (
    <section data-testid="landing-pricing" style={{ display: "grid", gap: 24 }}>
      <header style={{ maxWidth: 720 }}>
        <span style={{ background: BRAND_COLORS.tealSoft, color: BRAND_COLORS.tealDeep, padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.3px" }}>
          PRICING
        </span>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(32px, 4vw, 48px)", margin: "16px 0 8px", letterSpacing: "-1px", fontWeight: 600 }}>
          Honest plans. AUD. No surprise add-ons.
        </h2>
        <p style={{ color: BRAND_COLORS.muted, fontSize: 17, margin: 0, lineHeight: 1.55 }}>
          {trialDays} days free on any plan. Cancel anytime — your data stays yours.
        </p>
      </header>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {plans.map((p) => {
          const popular = !!p.popular;
          return (
            <article
              key={p.id}
              data-testid={`landing-plan-${p.id}`}
              style={{
                background: popular ? BRAND_COLORS.ink : BRAND_COLORS.paper,
                color: popular ? BRAND_COLORS.cream : BRAND_COLORS.ink,
                border: `1px solid ${popular ? BRAND_COLORS.ink : BRAND_COLORS.border}`,
                borderRadius: 22,
                padding: "28px 26px",
                position: "relative",
                boxShadow: popular ? "0 24px 48px -28px rgba(15,22,20,0.45)" : "none",
              }}
            >
              {popular && (
                <span style={{ position: "absolute", top: -12, left: 26, background: BRAND_COLORS.teal, color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.5px" }}>
                  MOST POPULAR
                </span>
              )}
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
              <div style={{ color: popular ? "#D9DEDC" : BRAND_COLORS.muted, fontSize: 14, marginBottom: 18, minHeight: 40 }}>{p.tagline}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 18 }}>
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: 48, fontWeight: 600 }}>${p.monthly_aud}</span>
                <span style={{ color: popular ? "#D9DEDC" : BRAND_COLORS.muted, fontSize: 14 }}>AUD / month</span>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "grid", gap: 10 }}>
                {(p.features || []).map((f) => (
                  <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, lineHeight: 1.4 }}>
                    <span style={{ color: popular ? BRAND_COLORS.teal : BRAND_COLORS.tealDeep, fontWeight: 700, marginTop: 1 }}>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/get-started"
                data-testid={`landing-plan-${p.id}-cta`}
                style={{
                  display: "block", textAlign: "center",
                  background: popular ? BRAND_COLORS.teal : "transparent",
                  color: popular ? "#fff" : BRAND_COLORS.ink,
                  border: popular ? "none" : `1.5px solid ${BRAND_COLORS.ink}`,
                  padding: "12px 16px", borderRadius: 999, fontWeight: 700, fontSize: 14,
                  textDecoration: "none",
                }}
              >
                Start free trial
              </Link>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function SocialProof() {
  // Three placeholder quotes from real-world-feeling trainers. Swap with actuals
  // once we have launch testimonials — until then these set tone & objection-handling.
  const quotes = [
    {
      q: "I used to bounce between Calendly, MailerLite, Google Forms and a spreadsheet. Puppl just is all of it — and Sam writes my session reports.",
      a: "Mira K.",
      role: "Pet Pals Dog Training · Brisbane",
    },
    {
      q: "Built for trainers who actually train dogs. The waitlist + capacity logic alone saved me 5 hours a week.",
      a: "Tom W.",
      role: "Westside Working Dogs · Perth",
    },
    {
      q: "Stripe Connect dropped my admin to zero. Clients pay, money lands, GST is calculated. I just train.",
      a: "Aisha P.",
      role: "Gentle Lead K9 · Sydney",
    },
  ];
  return (
    <section data-testid="landing-social-proof" style={{ display: "grid", gap: 24 }}>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(28px, 3.5vw, 40px)", margin: 0, letterSpacing: "-0.8px", fontWeight: 600, maxWidth: 680 }}>
        Trusted by trainers who&apos;d rather be on the field.
      </h2>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {quotes.map((q, i) => (
          <blockquote
            key={i}
            data-testid={`landing-quote-${i}`}
            style={{
              background: BRAND_COLORS.parchment,
              border: `1px solid ${BRAND_COLORS.border}`,
              borderRadius: 18,
              padding: "24px 22px",
              margin: 0,
              fontSize: 15,
              lineHeight: 1.55,
              color: BRAND_COLORS.inkSoft,
            }}
          >
            <p style={{ margin: "0 0 16px", fontFamily: "'Fraunces', serif", fontSize: 17 }}>&ldquo;{q.q}&rdquo;</p>
            <footer style={{ fontSize: 13, color: BRAND_COLORS.muted }}>
              <strong style={{ color: BRAND_COLORS.ink }}>{q.a}</strong> · {q.role}
            </footer>
          </blockquote>
        ))}
      </div>
    </section>
  );
}

function Faq() {
  const items = [
    {
      q: "How is Puppl different from a generic CRM?",
      a: "Built for dog trainers, not adapted from a gym SaaS. Waitlists, capacity, intake forms, course LMS, Stripe Connect, GST-aware AU invoicing, and Sam the AI sidekick — all out of the box.",
    },
    {
      q: "What does the 14-day trial include?",
      a: "Everything on the Growth plan: unlimited services + courses, 200 SMS, 1,000 Sam credits. No card required upfront. You only enter payment when you choose to keep going.",
    },
    {
      q: "Do I keep my data if I cancel?",
      a: "Yes. Your data is yours. Export clients, dogs, bookings, and notes to CSV anytime — even after a cancelled subscription (reads stay open).",
    },
    {
      q: "Where does payment from my clients go?",
      a: "Direct to your Stripe account via Stripe Connect. Puppl takes a small platform fee, the rest hits your bank — no holding period.",
    },
    {
      q: "Is Puppl based in Australia?",
      a: "Yes — built in Brisbane by a dog trainer for dog trainers. AUD pricing, GST-aware invoicing, AU-friendly timezones and English everywhere.",
    },
  ];
  return (
    <section data-testid="landing-faq" style={{ display: "grid", gap: 16, maxWidth: 820 }}>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(28px, 3.5vw, 40px)", margin: 0, letterSpacing: "-0.8px", fontWeight: 600 }}>
        Frequently asked, briefly answered.
      </h2>
      <div style={{ display: "grid", gap: 10 }}>
        {items.map((it, i) => (
          <details
            key={i}
            data-testid={`landing-faq-${i}`}
            style={{
              background: BRAND_COLORS.paper,
              border: `1px solid ${BRAND_COLORS.border}`,
              borderRadius: 14,
              padding: "16px 20px",
            }}
          >
            <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 16, color: BRAND_COLORS.ink, listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{it.q}</span>
              <span style={{ color: BRAND_COLORS.muted, fontSize: 20 }}>+</span>
            </summary>
            <p style={{ margin: "12px 0 0", color: BRAND_COLORS.muted, fontSize: 15, lineHeight: 1.6 }}>{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function CallToAction() {
  return (
    <section
      id="cta"
      style={{
        background: BRAND_COLORS.ink,
        color: BRAND_COLORS.cream,
        borderRadius: 28,
        padding: "48px 44px",
        display: "flex",
        gap: 28,
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        boxShadow: "0 30px 60px -40px rgba(15,22,20,0.5)",
      }}
    >
      <div style={{ maxWidth: 520 }}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 36, margin: 0, fontWeight: 600, letterSpacing: "-0.5px" }}>
          Spin up your dog training business in 90 seconds.
        </h2>
        <p style={{ marginTop: 12, color: "#D9DEDC", lineHeight: 1.6, fontSize: 16 }}>
          One signup. We provision your tenant, branded portal, and Sam — you start booking clients.
        </p>
      </div>
      <Link
        to="/get-started"
        data-testid="cta-bottom-signup"
        style={{
          background: BRAND_COLORS.teal,
          color: "#fff",
          padding: "18px 30px",
          borderRadius: 999,
          fontWeight: 600,
          fontSize: 17,
          textDecoration: "none",
          boxShadow: "0 8px 24px -10px rgba(51,192,149,0.7)",
        }}
      >
        Start free trial →
      </Link>
    </section>
  );
}

function PupplFooter() {
  return (
    <footer
      style={{
        borderTop: `1px solid ${BRAND_COLORS.border}`,
        padding: "32px 32px 48px",
        maxWidth: 1180,
        margin: "0 auto",
        color: BRAND_COLORS.muted,
        fontSize: 14,
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
      }}
    >
      <span>© {new Date().getFullYear()} {BRAND_NAME}. Built in Australia, by a dog trainer for dog trainers.</span>
      <span style={{ display: "flex", gap: 18 }}>
        <Link to="/privacy" style={{ color: "inherit" }}>Privacy</Link>
        <Link to="/terms" style={{ color: "inherit" }}>Terms</Link>
        <a href="mailto:support@puppl.app" style={{ color: "inherit" }}>support@puppl.app</a>
      </span>
    </footer>
  );
}

function PupplMark({ size = 36 }) {
  return (
    <img
      src={BRAND_LOGO}
      alt="Puppl"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: 14,
        objectFit: "cover",
        background: BRAND_COLORS.teal,
        display: "block",
      }}
    />
  );
}

export { PupplMark };
